import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import DownloadIcon from '@mui/icons-material/Download';
import {
  Button,
  Grid2 as Grid,
  List,
  ListItem,
  ListItemText,
  Paper,
  Skeleton,
  Stack,
  Typography,
} from '@mui/material';
import { useQuery } from '@tanstack/react-query';
import { useState } from 'react';
import { Link as RouterLink, useNavigate, useParams } from 'react-router-dom';

import { cancelInvoice, deletePayment, downloadInvoicePdf, readInvoice } from '../../api/admin/endpoints';
import type { InvoiceDetail } from '../../api/admin/types';
import { useAppMutation } from '../../api/useAppMutation';
import { ErrorNotice } from '../../components/ErrorNotice';
import { formatDate, formatDateTime, formatMoney } from '../../components/format';
import { FormDialog } from '../../components/FormDialog';
import { InvoiceLines } from '../../components/InvoiceLines';
import { InvoiceStatusChip } from '../../components/InvoiceStatusChip';
import { PageHeader } from '../../components/PageHeader';
import { PartyBlock } from '../../components/PartyBlock';
import { saveBlob } from '../../components/saveBlob';
import { useLanguage } from '../../i18n/LanguageContext';

import { RecordPaymentDialog } from './RecordPaymentDialog';

const INVALIDATES: Array<string> = ['invoice', 'invoices', 'tenant', 'tenants', 'audit'];

/** One document: its parties and lines, its payments, and what can still be done with it (R0006). */
export const InvoiceDetailPage = () => {
  const { invoiceId = '' } = useParams();
  const { language, t } = useLanguage();
  const navigate = useNavigate();
  const query = useQuery({ queryKey: ['invoice', invoiceId], queryFn: () => readInvoice(invoiceId) });
  const [dialog, setDialog] = useState<'payment' | 'cancel' | null>(null);
  const [downloadError, setDownloadError] = useState<unknown>(null);
  const remove = useAppMutation((paymentId: string) => deletePayment(invoiceId, paymentId), INVALIDATES);
  const cancel = useAppMutation(
    () => cancelInvoice(invoiceId),
    INVALIDATES,
    () => setDialog(null),
  );
  const invoice: InvoiceDetail | undefined = query.data;

  if (query.isLoading) {
    return <Skeleton variant="rounded" height={420} />;
  }

  if (!invoice) {
    return (
      <>
        <ErrorNotice error={query.error} onRetry={() => void query.refetch()} />
        <Button startIcon={<ArrowBackIcon />} onClick={() => navigate('/platform/invoices')}>
          {t('invoice.back')}
        </Button>
      </>
    );
  }

  const money = (minor: number): string => formatMoney(minor, invoice.currency, language);
  const open = invoice.status === 'ISSUED' || invoice.status === 'PARTIALLY_PAID';
  const isProforma = invoice.documentType === 'PROFORMA';
  const balance = invoice.totalMinor - invoice.paidMinor;

  const download = async (): Promise<void> => {
    setDownloadError(null);

    try {
      saveBlob(
        await downloadInvoicePdf(invoice.id),
        `${isProforma ? 'zalohova-faktura' : 'faktura'}-${invoice.number}.pdf`,
      );
    } catch (caught) {
      setDownloadError(caught);
    }
  };

  return (
    <>
      <Button startIcon={<ArrowBackIcon />} onClick={() => navigate('/platform/invoices')} sx={{ mb: 1 }}>
        {t('invoice.back')}
      </Button>
      <PageHeader
        title={`${t(`documentType.${invoice.documentType}` as const)} ${invoice.number}`}
        subtitle={invoice.tenantName}
        action={
          <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
            <Button variant="outlined" startIcon={<DownloadIcon />} onClick={() => void download()}>
              {t('invoice.download')}
            </Button>
            {isProforma && open ? (
              <Button variant="contained" onClick={() => setDialog('payment')}>
                {t('invoice.recordPayment')}
              </Button>
            ) : null}
            {isProforma && invoice.status === 'ISSUED' && invoice.paidMinor === 0 ? (
              <Button color="warning" onClick={() => setDialog('cancel')}>
                {t('invoice.cancel')}
              </Button>
            ) : null}
          </Stack>
        }
      />
      <ErrorNotice error={downloadError ?? remove.error} />

      <Grid container spacing={3}>
        <Grid size={{ xs: 12, lg: 8 }}>
          <Paper variant="outlined" sx={{ p: 3 }}>
            <Stack direction="row" spacing={2} alignItems="center" sx={{ mb: 2 }}>
              <InvoiceStatusChip status={invoice.status} overdue={invoice.overdue} />
              <Typography variant="body2" color="text.secondary">
                {t('field.issuedOn')} {formatDate(invoice.issuedOn, language)} · {t('field.dueOn')}{' '}
                {formatDate(invoice.dueOn, language)} · {t('field.variableSymbol')} {invoice.variableSymbol}
              </Typography>
            </Stack>
            <Grid container spacing={3} sx={{ mb: 3 }}>
              <Grid size={{ xs: 12, sm: 6 }}>
                <PartyBlock heading={t('invoice.supplier')} party={invoice.supplier} />
              </Grid>
              <Grid size={{ xs: 12, sm: 6 }}>
                <PartyBlock heading={t('invoice.buyer')} party={invoice.buyer} />
              </Grid>
            </Grid>
            <InvoiceLines
              lines={invoice.lines}
              netMinor={invoice.netMinor}
              vatMinor={invoice.vatMinor}
              totalMinor={invoice.totalMinor}
              vatPayer={invoice.vatPayer}
              vatRateBasisPoints={invoice.vatRateBasisPoints}
              currency={invoice.currency}
            />
          </Paper>
        </Grid>

        <Grid size={{ xs: 12, lg: 4 }}>
          <Stack spacing={3}>
            <Paper variant="outlined" sx={{ p: 3 }}>
              <Typography variant="h6" sx={{ mb: 1 }}>
                {t('invoice.payments')}
              </Typography>
              {invoice.payments.length === 0 ? (
                <Typography variant="body2" color="text.secondary">
                  {t('invoice.noPayments')}
                </Typography>
              ) : (
                <List dense disablePadding>
                  {invoice.payments.map((payment) => (
                    <ListItem
                      key={payment.id}
                      disableGutters
                      secondaryAction={
                        open ? (
                          <Button
                            size="small"
                            disabled={remove.isPending}
                            onClick={() => remove.mutate(payment.id)}
                          >
                            {t('invoice.deletePayment')}
                          </Button>
                        ) : null
                      }
                    >
                      <ListItemText
                        primary={`${money(payment.amountMinor)} · ${formatDate(payment.receivedOn, language)}`}
                        secondary={[
                          payment.reference,
                          payment.recordedByEmail,
                          formatDateTime(payment.createdAt, language),
                        ]
                          .filter(Boolean)
                          .join(' · ')}
                      />
                    </ListItem>
                  ))}
                </List>
              )}
              <Stack direction="row" justifyContent="space-between" sx={{ mt: 2 }}>
                <Typography variant="body2" color="text.secondary">
                  {t('field.paid')}
                </Typography>
                <Typography variant="body2">{money(invoice.paidMinor)}</Typography>
              </Stack>
              <Stack direction="row" justifyContent="space-between">
                <Typography variant="subtitle2">
                  {balance < 0 ? t('invoice.overpaid') : t('invoice.remaining')}
                </Typography>
                <Typography variant="subtitle2">{money(Math.abs(balance))}</Typography>
              </Stack>
            </Paper>

            {invoice.order ? (
              <Paper variant="outlined" sx={{ p: 3 }}>
                <Typography variant="h6" sx={{ mb: 1 }}>
                  {t('invoice.order')}
                </Typography>
                <Typography variant="body2">
                  {t('invoice.orderSummary', {
                    kind: t(`invoice.orderKind.${invoice.order.kind}` as const),
                    months: invoice.order.months,
                  })}
                </Typography>
                <Stack spacing={1} sx={{ mt: 2 }} alignItems="flex-start">
                  <Button component={RouterLink} to={`/platform/tenants/${invoice.tenantId}`} size="small">
                    {invoice.tenantName}
                  </Button>
                  {isProforma && invoice.order.invoiceId ? (
                    <Button
                      component={RouterLink}
                      to={`/platform/invoices/${invoice.order.invoiceId}`}
                      size="small"
                    >
                      {t('invoice.openSettled')}
                    </Button>
                  ) : null}
                  {!isProforma ? (
                    <Button
                      component={RouterLink}
                      to={`/platform/invoices/${invoice.order.proformaId}`}
                      size="small"
                    >
                      {t('invoice.openProforma')}
                    </Button>
                  ) : null}
                </Stack>
                {invoice.proformaNumber ? (
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
                    {t('invoice.settles', { number: invoice.proformaNumber })}
                  </Typography>
                ) : null}
              </Paper>
            ) : null}
          </Stack>
        </Grid>
      </Grid>

      {dialog === 'payment' ? (
        <RecordPaymentDialog invoice={invoice} onClose={() => setDialog(null)} />
      ) : null}
      {dialog === 'cancel' ? (
        <FormDialog
          title={t('invoice.cancel')}
          onClose={() => setDialog(null)}
          onSubmit={() => cancel.mutate(undefined)}
          submitLabel={t('invoice.cancel')}
          isBusy={cancel.isPending}
          error={cancel.error}
        >
          <Typography>{t('invoice.cancelConfirm', { number: invoice.number })}</Typography>
        </FormDialog>
      ) : null}
    </>
  );
};
